import axios from 'axios';
import API_URL from '../config/api';
import LANDING_URL from '../config/landing';

// ⭐ Cliente axios compartido para las rutas autenticadas del POS.
//
// Dashboard.js y MetricsPanel.js (panel de admin) ya tenían cada uno su
// propia copia de exactamente este mismo patrón -- un axios.create() con
// un interceptor que adjunta `Authorization: Bearer <token>` leyendo
// sessionStorage. Acá se vuelve un solo archivo, y además se le suma algo
// que hoy NO existe en ningún lado: manejo centralizado de sesión vencida
// (401). Sin esto, si el JWT expira a mitad de sesión (son válidos hasta
// 7 días), cada pantalla del POS simplemente ve fallar su petición y
// según el catch de cada componente eso se traduce en un CustomAlert
// confuso ("Error interno del servidor") en vez de mandar al usuario a
// iniciar sesión de nuevo.
//
// ⭐ sessionStorage (no localStorage) a propósito: incidente 2026-07-28
// (BUG CRITICO: sesión de POS se filtraba entre pestañas porque el token
// vivía en localStorage, compartido por todas las pestañas del mismo
// navegador). Guardarlo en sessionStorage aísla la sesión por pestaña --
// leerlo acá desde el mismo storage y con la misma clave ('token') que ya
// usa App.js es lo que mantiene esa garantía, no algo que este archivo
// decida por su cuenta.
//
// Alcance de esta ronda de limpieza: se crea este cliente y se deja listo
// para usar en código nuevo (y en el próximo módulo, CRM/Conversaciones),
// pero NO se migran automáticamente los ~83 call sites de axios.get/post
// que hoy arman `headers: { Authorization: ... }` a mano en cerca de 20
// archivos del POS. La mayoría de esas llamadas mezcla headers con otras
// opciones (params, responseType: 'blob', FormData) y cada una habría que
// revisarla individualmente para no romper una petición real en un POS
// que ya está en producción con clientes pagando -- ese trabajo mecánico
// se deja para una ronda aparte, con más tiempo para verificar cada
// archivo uno por uno, en vez de forzarlo en este mismo pase.
// ⭐ La URL de la landing vive en config/landing.js (antes estaba duplicada
// aquí y en App.js con un valor por defecto de localhost que rompía el
// redirect por sesión expirada en producción — ver ese archivo).
const apiClient = axios.create({ baseURL: API_URL });

apiClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    // Toda modificación que debe aprobar MARKETOS responde 202. Se avisa
    // desde este punto común sin depender de la pantalla que la originó.
    if (response.status === 202 && response.data?.approval_required) {
      window.dispatchEvent(new CustomEvent('marketos:approval-requested', {
        detail: { message: response.data.message }
      }));
    }
    return response;
  },
  (error) => {
    // 401 = token inválido, vencido, o revocado -- no hay nada que un
    // componente individual pueda hacer con eso salvo mandar al usuario a
    // iniciar sesión de nuevo. Se centraliza acá para que ningún endpoint
    // nuevo tenga que reinventar ese manejo.
    if (error.response?.status === 401) {
      sessionStorage.clear();
      window.location.replace(LANDING_URL);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
